import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Plane,
  Wrench,
  Warehouse,
  FileCheck,
  Fuel,
  Receipt,
  Phone,
  Mail,
  MapPin,
  Download,
} from "lucide-react";
import Layout from "../components/Layout";
import InquiryForm from "../components/InquiryForm";
import TwoDoorsExperience from "../components/TwoDoorsExperience";
import TwoDoorsCollapsed from "../components/TwoDoorsCollapsed";
import useTravelAssessment from "../hooks/useTravelAssessment";

const SERVICES = [
  {
    icon: Plane,
    title: "Flight Coordination",
    body:
      "Scheduling and sourcing qualified, vetted flight crews for every mission.",
  },
  {
    icon: Wrench,
    title: "Maintenance Management",
    body:
      "Scheduled inspections, repairs, and airworthiness oversight handled with trusted shops.",
  },
  {
    icon: Warehouse,
    title: "Hangar & Storage",
    body:
      "Secure hangar storage and ground handling—your aircraft protected and ready.",
  },
  {
    icon: FileCheck,
    title: "Records & Compliance",
    body:
      "Logbooks, regulatory documentation, and aircraft records kept current and organized.",
  },
  {
    icon: Fuel,
    title: "Fueling & Servicing",
    body:
      "Fuel, cleaning, and detailing coordinated through Steck Aviation Management services.",
  },
  {
    icon: Receipt,
    title: "Cost & Vendor Management",
    body:
      "Invoicing, vendor relationships, and operating-budget oversight on your behalf.",
  },
];

const AGREEMENTS = [
  {
    tag: "01",
    title: "Aircraft Management Agreement",
    body:
      "The master framework—scope of services, fees, responsibilities, and the operating relationship between owner and management company.",
  },
  {
    tag: "02",
    title: "Operating Agreement",
    body:
      "Defines how the aircraft is operated under Part 91 owner-operator rules, with clear boundaries on operational control.",
  },
  {
    tag: "03",
    title: "Dry Lease Agreement",
    body:
      "When ownership and operation are structurally separated, the dry lease aligns crew, control, and tax treatment correctly.",
  },
  {
    tag: "04",
    title: "Joint Owner / Operator Agreement",
    body:
      "Where multiple owners share aircraft, the joint owner/operator structure governs the exchange of flight time and cost allocation.",
  },
];

const About = () => {
  // The Two Doors tool is available here (a vestibule destination) via the
  // collapsed tab — but it does NOT auto-open. Force-opening a full-screen
  // overlay on arrival read as a broken/locked scroll, so arrivals just see the
  // page; the tab lets anyone open the tool on their own terms.
  const {
    open: assessmentOpen,
    openAssessment, closeAssessment, onComplete: onAssessmentComplete,
  } = useTravelAssessment({ openOnScroll: false });

  const openTwoDoors = (entry) => {
    if (entry) {
      try { sessionStorage.setItem("steck_2d_entry", entry); } catch (e) { /* ignore */ }
    }
    openAssessment("tab");
  };

  return (
  <Layout>
    <TwoDoorsExperience open={assessmentOpen} onClose={closeAssessment} onComplete={onAssessmentComplete} />
    {/* Phones: fixed reopen tab in the old "Gauge your needs" spot (the in-hero
        widget below is desktop-only). */}
    <TwoDoorsCollapsed mobileTab visible={!assessmentOpen} onOpen={openTwoDoors} />
    <SEO
      title="Management | Steck Aviation Management"
      description="Steck Aviation Management is a private aircraft management firm operating exclusively for owners. Learn about our approach, structure, and team."
    />
    {/* Management-page label system, kept strict so the page reads as ONE
        system: every section eyebrow carries the brand cyan (the eye-catching,
        uniform "signal" tier); detail/sub-labels stay slate (muted secondary
        tier); headings ivory. The mgmt-eyebrow rule overrides .font-mono-label's
        slate default, which is why the eyebrows had been rendering muted. */}
    <style>{`.mgmt-eyebrow { color: #67E8F9 !important; }`}</style>
    {/* HERO—full aircraft photo shown in its entirety, title + copy below it */}
    <section style={{ position: "relative", background: "#03101C", overflow: "hidden" }}>
      {/* The whole aircraft, uncropped, full width. height:auto lets the photo
          keep its own proportions at every screen size—no crop, no overflow. */}
      <div style={{ position: "relative", width: "100%" }}>
        <img
          src="/images/management-hangar-jet.png"
          alt="Steck Aviation Beechjet 400A in the hangar"
          style={{ display: "block", width: "100%", height: "auto" }}
        />
        {/* Top scrim keeps the fixed nav legible over the photo; a soft bottom
            fade melts the photo down into the copy area rather than ending on a
            hard edge. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(to bottom, rgba(3,16,28,0.72) 0%, rgba(3,16,28,0.16) 15%, transparent 36%, transparent 80%, rgba(3,16,28,0.85) 100%)",
          }}
        />
      </div>

      {/* Title + copy—below the photo, on the dark ground, so the type sits on
          its own clean field instead of fighting the image behind it. */}
      <div
        style={{
          position: "relative",
          maxWidth: 1500,
          margin: "0 auto",
          padding:
            "clamp(28px,3.4vw,52px) clamp(24px,6vw,80px) clamp(52px,7vw,96px)",
        }}
      >
        {/* Two Doors widget — a small tab in the empty space to the right of the
            title. It scrolls with the page (absolute, not fixed) and is sized so
            it never covers the title or copy. Hidden on narrower screens. */}
        <div className="about-td-widget">
          <TwoDoorsCollapsed visible={!assessmentOpen} onOpen={openTwoDoors} compact />
        </div>
        <style>{`
          .about-td-widget { position: absolute; top: clamp(28px,3.4vw,52px); right: clamp(24px,6vw,80px); z-index: 3; }
          @media (max-width: 1120px) { .about-td-widget { display: none; } }
        `}</style>
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            color: "#67E8F9",
            marginBottom: 20,
            textTransform: "uppercase",
            fontWeight: 500,
          }}
        >
          Aircraft Management
        </div>
        <h1
          style={{
            fontFamily: "Fraunces, serif",
            fontSize: "clamp(36px, 4.8vw, 64px)",
            color: "#fff",
            lineHeight: 1.04,
            margin: 0,
            maxWidth: 780,
          }}
        >
          We manage your aircraft.
          <br />
          <span style={{ color: "rgba(255,255,255,0.50)" }}>
            You enjoy the ownership.
          </span>
        </h1>
        <p
          style={{
            marginTop: "clamp(14px,2vh,22px)",
            color: "rgba(255,255,255,0.58)",
            fontSize: 16,
            lineHeight: 1.76,
            maxWidth: 520,
            fontWeight: 300,
          }}
        >
          Not a charter operator. Not a broker. A management company working
          exclusively for owners—on every detail of operating your aircraft.
        </p>
        <div style={{ marginTop: "clamp(20px,3vh,32px)", display: "flex", gap: 14, flexWrap: "wrap" }}>
          <a href="#inquiry" className="btn-primary" data-testid="about-cta-talk">
            Let&apos;s talk <ArrowRight size={14} />
          </a>
          <Link to="/library" className="btn-ghost">
            Read our research
          </Link>
        </div>
      </div>
    </section>

    {/* POSITIONING—we are NOT a charter --------------------- */}
    <section className="relative border-t border-[var(--border-soft)] py-32">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-mono-label mgmt-eyebrow mb-5">What we are</div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--ivory)] leading-tight">
            A management company.
            <br />
            <span
              className="text-[var(--ivory-dim)]"
              
            >
              Not a charter operator.
            </span>
          </h2>
        </div>
        <div className="md:col-span-7 space-y-6 text-[var(--slate)] leading-[1.85] font-light text-lg">
          <p>
            We do not sell, offer, or provide flights. Neither Steck Aviation
            nor Steck Aviation Management operates flights for hire.
          </p>
          <p>
            Instead, we represent <span className="text-[var(--ivory)]">the owner</span>.
            From sourcing qualified pilots to scheduling maintenance, arranging
            storage, and keeping your aircraft mission-ready—we handle the
            details on your behalf.
          </p>
          <p>
            Every owner relationship is governed by clear, written agreements
            so that responsibilities, costs, and expectations are never in
            question.
          </p>
        </div>
      </div>
    </section>

    {/* SERVICES—six-pillar grid ------------------------------ */}
    <section
      className="relative border-t border-[var(--border-soft)] py-32"
      data-testid="about-services-section"
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="font-mono-label mgmt-eyebrow mb-5">Services</div>
        <h2 className="font-display text-4xl md:text-5xl text-[var(--ivory)] leading-tight max-w-3xl">
          The full scope of ownership.
          <span
            className="text-[var(--ivory-dim)]"
            
          >
            {" "}Coordinated.
          </span>
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="bg-[var(--bg)] p-10 hover:bg-[var(--surface)] transition-colors group"
                data-testid={`service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start gap-5">
                  <div className="font-mono-label text-[var(--slate-dim)] pt-1">
                    0{idx + 1}
                  </div>
                  <Icon
                    size={22}
                    className="text-[#67E8F9] mt-0.5 shrink-0"
                  />
                </div>
                <h3 className="font-display text-2xl text-[var(--ivory)] mt-6 group-hover:text-[var(--blue-soft)] transition-colors">
                  {s.title}
                </h3>
                <p className="mt-4 text-[var(--slate)] text-[15px] leading-[1.8] font-light">
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* MAINTENANCE—what it actually means */}
    <section className="relative border-t border-[var(--border-soft)] py-32">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            color: "#67E8F9",
            textTransform: "uppercase",
            fontWeight: 500,
            marginBottom: 20,
          }}
        >
          Maintenance Management
        </div>
        <h2 className="font-display text-4xl md:text-5xl text-[var(--ivory)] leading-tight max-w-3xl">
          Managed directly.
          <span className="text-[var(--ivory-dim)]"> Not handed off.</span>
        </h2>
        <p
          style={{
            marginTop: 24,
            color: "var(--slate)",
            lineHeight: 1.82,
            fontSize: 17,
            maxWidth: 620,
            fontWeight: 300,
          }}
        >
          Every squawk, every inspection interval, every AD—tracked,
          coordinated, and resolved before it becomes an airworthiness issue.
          We don't hand maintenance off and wait for a call. We handle it.
        </p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)]">
          {[
            {
              label: "Inspection Coordination",
              items: [
                "Annual inspections",
                "Phase checks and manufacturer intervals",
                "Engine and APU cycle tracking",
                "Unscheduled maintenance events",
              ],
            },
            {
              label: "AD / SB Compliance",
              items: [
                "Airworthiness Directive (AD) tracking",
                "Service Bulletin (SB) review and status",
                "Mandatory vs. optional SB classification",
                "Compliance documentation for resale",
              ],
            },
            {
              label: "Squawk Management",
              items: [
                "Discrepancy identification and logging",
                "Shop selection and write-up coordination",
                "Parts sourcing and procurement tracking",
                "Return-to-service verification and sign-off",
              ],
            },
            {
              label: "Records & Logbooks",
              items: [
                "Airframe, engine, and avionics logs",
                "Maintenance release documentation",
                "Pre-purchase audit preparation",
                "Digital and physical records organization",
              ],
            },
          ].map((block) => (
            <div key={block.label} className="bg-[var(--bg)] p-10">
              <div
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  color: "#9CB1C3",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  marginBottom: 20,
                }}
              >
                {block.label}
              </div>
              <ul style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {block.items.map((item) => (
                  <li
                    key={item}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      color: "rgba(255,255,255,0.84)",
                      fontSize: 15,
                      lineHeight: 1.65,
                    }}
                  >
                    <span
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        background: "#67E8F9",
                        flexShrink: 0,
                        marginTop: 8,
                      }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* OPERATING MODEL—four key agreements ------------------- */}
    <section className="relative border-t border-[var(--border-soft)] py-32">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="font-mono-label mgmt-eyebrow mb-5">Governance</div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--ivory)] leading-tight">
            Four agreements.
            <br />
            <span
              className="text-[var(--ivory-dim)]"
              
            >
              Clarity from day one.
            </span>
          </h2>
          <p className="mt-7 text-[var(--slate)] leading-[1.8] font-light max-w-md">
            Owner protection and operational clarity begin on paper.
            We use a defined set of agreements so the legal architecture
            of ownership matches the operational reality.
          </p>
        </div>
        <div className="md:col-span-8 space-y-px bg-[var(--border)]">
          {AGREEMENTS.map((a) => (
            <div
              key={a.tag}
              className="bg-[var(--bg)] p-10 grid grid-cols-1 md:grid-cols-12 gap-6"
              data-testid={`agreement-${a.tag}`}
            >
              <div className="md:col-span-2 font-mono-label text-[var(--blue-soft)]">
                {a.tag}
              </div>
              <div className="md:col-span-10">
                <h3 className="font-display text-2xl text-[var(--ivory)]">
                  {a.title}
                </h3>
                <p className="mt-3 text-[var(--slate)] text-[15px] leading-[1.85] font-light">
                  {a.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* INFRASTRUCTURE—Mineola HQ + East Texas region -------- */}
    <section className="relative border-t border-[var(--border-soft)] py-32">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6">
          <div className="font-mono-label mgmt-eyebrow mb-5">
            Where we operate
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--ivory)] leading-tight">
            Mineola, Texas.
            <br />
            <span
              className="text-[var(--ivory-dim)]"

            >
              Serving owners across four states.
            </span>
          </h2>
          <p className="mt-7 text-[var(--slate)] leading-[1.85] font-light max-w-xl text-lg">
            Owners across Texas, Louisiana, Oklahoma, and Arkansas
            rely on Steck Aviation Management for end-to-end
            coordination—anchored at our Mineola headquarters and integrated
            with regional airport infrastructure.
          </p>
        </div>
        <div className="md:col-span-6 space-y-px bg-[var(--border)]">
          {[
            ["Headquarters", "1251 Bromberg Street, Mineola, Texas 75773"],
            ["Direct", "903-539-9000"],
            ["Email", "contact@steckaviationllc.com"],
            ["Service area", "Texas, Louisiana, Oklahoma, Arkansas"],
          ].map(([label, val]) => (
            <div
              key={label}
              className="bg-[var(--bg)] px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div className="font-mono-label text-[var(--blue-soft)]">{label}</div>
              <div className="text-[15px] text-[var(--ivory)] md:text-right font-normal tracking-tight">
                {val}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* DOCUMENTS ---------------------------------------------- */}
    <section className="relative border-t border-[var(--border-soft)] py-32">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="font-mono-label mgmt-eyebrow mb-5">Documents</div>
        <h2 className="font-display text-4xl md:text-5xl text-[var(--ivory)] mb-14 leading-tight">
          Briefing materials.
          <span
            className="text-[var(--ivory-dim)]"
            
          >
            {" "}For download.
          </span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)]">
          <a
            href="/documents/aircraft-management-services.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-[var(--bg)] hover:bg-[var(--surface)] p-10 transition-colors"
          >
            <div className="font-mono-label text-[var(--slate)] mb-4">
              Executive overview
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[var(--ivory)] group-hover:text-[var(--blue-soft)] transition-colors">
              Aircraft Management Services
            </h3>
            <p className="text-[var(--slate)] text-sm mt-3 leading-relaxed font-light max-w-md">
              The complete services overview, operating model, and the four
              governance agreements underpinning every owner relationship.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 font-mono-label text-[var(--ivory)] group-hover:text-[var(--blue-soft)] transition-colors">
              Download PDF <Download size={12} />
            </div>
          </a>
          <a
            href="/documents/beechjet-400a.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-[var(--bg)] hover:bg-[var(--surface)] p-10 transition-colors"
          >
            <div className="font-mono-label text-[var(--slate)] mb-4">
              Aircraft profile
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[var(--ivory)] group-hover:text-[var(--blue-soft)] transition-colors">
              Beechjet 400A
            </h3>
            <p className="text-[var(--slate)] text-sm mt-3 leading-relaxed font-light max-w-md">
              Specifications, performance, and operating profile of the
              Beechjet 400A.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 font-mono-label text-[var(--ivory)] group-hover:text-[var(--blue-soft)] transition-colors">
              Download PDF <Download size={12} />
            </div>
          </a>
        </div>
      </div>
    </section>

    {/* INQUIRY ----------------------------------------------- */}
    <section
      id="inquiry"
      className="relative border-t border-[var(--border-soft)] py-32"
    >
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <div className="font-mono-label mgmt-eyebrow mb-5">
            Let&apos;s talk
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--ivory)] leading-tight">
            About managing your aircraft.
          </h2>
          <p className="mt-6 text-[var(--slate)] leading-[1.8] font-light">
            Send a note and a principal at Steck will respond
            directly—usually within one business day.
          </p>
          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-center gap-3 text-[var(--ivory)]">
              <Phone size={14} className="text-[var(--blue-soft)]" /> 903-539-9000
            </li>
            <li className="flex items-center gap-3">
              <Mail size={14} className="text-[var(--blue-soft)]" />
              <a
                className="editorial-link"
                href="mailto:contact@steckaviationllc.com"
              >
                contact@steckaviationllc.com
              </a>
            </li>
            <li className="flex items-start gap-3 text-[var(--slate)]">
              <MapPin size={14} className="text-[var(--blue-soft)] mt-1 shrink-0" />
              <span>
                1251 Bromberg Street
                <br />
                Mineola, Texas 75773
              </span>
            </li>
          </ul>
        </div>
        <div className="md:col-span-7">
          <InquiryForm variant="compact" />
        </div>
      </div>
    </section>
  </Layout>
  );
};

export default About;
